import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminAdminMe as AdminMe } from '../generated/api/types.gen.ts';
import { meGet, meUpdate } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';

export type { AdminMe };

/** Nøkkelen eies her og bare her (`react-ts-integration`, «Query-nøkler har én eier»). */
export const meNoekkel = ['me'] as const;

/**
 * `GET /v1/admin/me` — panelets første kall. Gir rollen, leietakernavnet og når
 * sesjonen tvinges ut (innlogging + 8 t). 401 og 403 kastes som ApiFeil og
 * håndteres av Sesjonsvakt; hooken selv prøver aldri på nytt.
 */
export function useMe(enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: meNoekkel,
    enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<AdminMe> => {
      const res = await meGet({ client: klient });
      const status = res.response?.status ?? 0;
      if (!res.response?.ok || !res.data) throw ApiFeil.fra(status, res.error ?? res.data);
      return res.data as AdminMe;
    },
  });
}

/**
 * Visningsnavnet (`PATCH /v1/admin/me`), 2–60 tegn — det eneste en panelbruker endrer på seg
 * selv. Rommet Hypoteser krever det før «Send inn» (409 `display_name_missing`). Svaret er
 * «meg» på nytt, så skallet og skjemaet leser navnet fra samme cache.
 */
export function useSettVisningsnavn() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (displayName: string): Promise<AdminMe> => {
      const res = await meUpdate({ client: klient, body: { displayName } });
      const status = res.response?.status ?? 0;
      if (!res.response?.ok || !res.data) throw ApiFeil.fra(status, res.error ?? res.data);
      return res.data as AdminMe;
    },
    onSuccess: (me) => {
      queryClient.setQueryData(meNoekkel, me);
    },
  });
}

/** Henter «meg» på nytt — når tjeneren sier at navnet mangler, men skjermen trodde noe annet. */
export function useOppfriskMeg() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: meNoekkel });
}
